import React, { useState, useEffect, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import {
  Plus,
  Play,
  Edit2,
  Trash2,
  Clock,
  Zap,
  CheckCircle,
  XCircle,
  AlertCircle,
  Eye,
  RefreshCw,
  ChevronRight,
  ChevronLeft,
  Settings,
  Database,
  FileText,
  Image,
  Calendar,
  Code,
  Variable,
  Globe,
  AlertTriangle,
  Pause,
  Copy,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Separator } from '../components/ui/separator';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { Progress } from '../components/ui/progress';
import { 
  templatesV2API, 
  runsV2API, 
  statsV2API, 
  integrationsAPI,
  heldQueueAPI 
} from '../lib/apiStoriesV2';
import { referenceAPI } from '../lib/api';
import { formatDate, formatDateTime, cn } from '../lib/utils';
import { toast } from 'sonner';
import { DynamicSelect } from '../components/DynamicSelect';

// Story Types
const STORY_TYPES = [
  { value: 'data_analysis', label: 'Data Analysis Report' },
  { value: 'market_intelligence', label: 'Market Intelligence' },
  { value: 'company_profile', label: 'Company Profile' },
  { value: 'earnings_summary', label: 'Earnings Summary' },
  { value: 'industry_roundup', label: 'Industry Roundup' },
  { value: 'breaking_news', label: 'Breaking News Brief' },
  { value: 'product_launch', label: 'Product Launch' },
  { value: 'interview_feature', label: 'Interview Feature' },
];

// Data Source Types
const DATA_SOURCE_TYPES = [
  { value: 'data_intelligence', label: 'Data Intelligence Records' },
  { value: 'csv', label: 'CSV Upload' },
  { value: 'api', label: 'REST API' },
  { value: 'google_sheets', label: 'Google Sheets' },
  { value: 'database', label: 'Database Query (SQL)' },
  { value: 'webhook', label: 'Webhook Trigger' },
  { value: 'rss', label: 'RSS Feed' },
];

// Image Providers
const IMAGE_PROVIDERS = [
  { value: 'none', label: 'None' },
  { value: 'dalle3', label: 'DALL-E 3' },
  { value: 'replicate', label: 'Stable Diffusion (Replicate)' },
];

// Image Styles
const IMAGE_STYLES = [
  { value: 'corporate', label: 'Corporate Data Visualisation' },
  { value: 'abstract', label: 'Abstract Financial' },
  { value: 'sector', label: 'Sector-Specific Illustration' },
  { value: 'editorial', label: 'Editorial Photography Style' },
  { value: 'minimal', label: 'Minimal Geometric' },
];

// Cron Presets
const CRON_PRESETS = [
  { label: 'Every hour', value: '0 * * * *' },
  { label: 'Every 6 hours', value: '0 */6 * * *' },
  { label: 'Daily 6am', value: '0 6 * * *' },
  { label: 'Daily 9am', value: '0 9 * * *' },
  { label: 'Weekdays 7am', value: '0 7 * * 1-5' },
  { label: 'Weekly Monday', value: '0 9 * * 1' },
];

// Output Fields
const OUTPUT_FIELDS = [
  { key: 'headline', label: 'Headline', required: true },
  { key: 'subheadline', label: 'Subheadline', required: false },
  { key: 'body', label: 'Body', required: true },
  { key: 'summary', label: 'Summary', required: false },
  { key: 'tags', label: 'Tags (array)', required: false },
  { key: 'category', label: 'Category', required: false },
  { key: 'seo_title', label: 'SEO Title', required: false },
  { key: 'seo_description', label: 'SEO Description', required: false },
  { key: 'word_count', label: 'Word Count', required: true },
  { key: 'confidence_score', label: 'Confidence Score', required: true },
  { key: 'data_gaps', label: 'Data Gaps (array)', required: false },
];

function StatusBadge({ status }) {
  const configs = {
    pending: { color: 'bg-amber-900/20 text-amber-400 border-amber-900/50', icon: Clock },
    running: { color: 'bg-blue-900/20 text-blue-400 border-blue-900/50 animate-pulse', icon: Zap },
    success: { color: 'bg-green-900/20 text-green-400 border-green-900/50', icon: CheckCircle },
    partial: { color: 'bg-amber-900/20 text-amber-400 border-amber-900/50', icon: AlertCircle },
    failed: { color: 'bg-red-900/20 text-red-400 border-red-900/50', icon: XCircle },
  };
  
  const config = configs[status] || configs.pending;
  const Icon = config.icon;

  return (
    <Badge className={`${config.color} border capitalize`}>
      <Icon className="w-3 h-3 mr-1" />
      {status}
    </Badge>
  );
}

function DemoModeBanner({ integrations, onSettingsClick }) {
  if (!integrations?.demo_mode_active) return null;
  
  return (
    <div className="bg-amber-900/20 border border-amber-700/50 rounded-lg p-4 flex items-center justify-between">
      <div className="flex items-center gap-3">
        <AlertTriangle className="w-5 h-5 text-amber-400" />
        <div>
          <p className="text-amber-300 font-medium">Running in Demo Mode</p>
          <p className="text-amber-400/80 text-sm">
            Some integrations are not configured. Stories will use mock data.
          </p>
        </div>
      </div>
      <Button variant="outline" size="sm" onClick={onSettingsClick} className="border-amber-600 text-amber-400 hover:bg-amber-900/30">
        <Settings className="w-4 h-4 mr-2" />
        Go to Settings
      </Button>
    </div>
  );
}

// Default system prompt
const DEFAULT_SYSTEM_PROMPT = `You are a senior UK business journalist and intelligence analyst generating structured content for a B2B media network. Return ONLY a valid JSON object — no preamble, no explanation, no markdown code fences.`;

// Initial form state
const initialFormState = {
  // Tab 1: Configuration
  name: '',
  story_type: '',
  description: '',
  publication_id: '',
  client_id: '',
  byline: '',
  max_tokens: 2000,
  temperature: 0.3,
  story_limit: 1,
  is_active: true,
  
  // Tab 2: Prompt Builder
  system_prompt: DEFAULT_SYSTEM_PROMPT,
  user_prompt: '',
  variables: [],
  
  // Tab 3: Data Source
  data_source: null,
  
  // Tab 4: WordPress Settings
  wordpress_settings: {
    post_status: 'draft',
    default_author: '',
    categories: '',
    tags_template: '',
    slug_template: '',
    duplicate_prevention: true,
    force_republish: false,
    image_provider: 'none',
    image_style: 'corporate',
    image_prompt_template: '',
    image_seed_fields: ['headline'],
    image_size: '1792x1024',
    alt_text_template: '',
    upload_to_wp: true,
  },
  
  // Tab 5: Schedule
  cron_expression: '',
  timezone: 'Europe/London',
  max_retries: 2,
  alert_email: '',
  run_concurrency: 'sequential',
  
  // Tab 6: Output Schema
  output_fields: {
    headline: true,
    subheadline: false,
    body: true,
    summary: false,
    tags: false,
    category: false,
    seo_title: false,
    seo_description: false,
    word_count: true,
    confidence_score: true,
    data_gaps: false,
  },
};

export function AutomatedStoriesV2() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('templates');
  
  // Data State
  const [templates, setTemplates] = useState([]);
  const [runs, setRuns] = useState([]);
  const [stats, setStats] = useState({});
  const [integrations, setIntegrations] = useState(null);
  const [heldQueue, setHeldQueue] = useState([]);
  
  // Reference Data
  const [publications, setPublications] = useState([]);
  const [clients, setClients] = useState([]);
  
  // Loading States
  const [loading, setLoading] = useState(true);
  const [runLoading, setRunLoading] = useState({});
  
  // Modal States
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [modalStep, setModalStep] = useState(0);
  const [formData, setFormData] = useState(initialFormState);
  
  // Run Detail
  const [selectedRun, setSelectedRun] = useState(null);
  const [runDetail, setRunDetail] = useState(null);
  
  // Variable Editor
  const [newVariable, setNewVariable] = useState({ name: '', description: '', example: '' });

  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    setLoading(true);
    try {
      const [
        templatesRes,
        runsRes,
        statsRes,
        integrationsRes,
        pubsRes,
        clientsRes,
        heldRes
      ] = await Promise.all([
        templatesV2API.getAll(),
        runsV2API.getAll({ page_size: 50 }),
        statsV2API.get(),
        integrationsAPI.getStatus(),
        referenceAPI.getPublications(),
        referenceAPI.getClients(),
        heldQueueAPI.getAll(),
      ]);
      
      setTemplates(templatesRes.data);
      setRuns(runsRes.data.runs || []);
      setStats(statsRes.data);
      setIntegrations(integrationsRes.data);
      setPublications(pubsRes.data);
      setClients(clientsRes.data);
      setHeldQueue(heldRes.data);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreateModal = () => {
    setEditingTemplate(null);
    setFormData(initialFormState);
    setModalStep(0);
    setShowTemplateModal(true);
  };

  const handleOpenEditModal = async (template) => {
    try {
      const response = await templatesV2API.getOne(template.id);
      const data = response.data;
      
      setEditingTemplate(data);
      setFormData({
        name: data.name || '',
        story_type: data.story_type || '',
        description: data.description || '',
        publication_id: data.publication_id || '',
        client_id: data.client_id || '',
        byline: data.byline || '',
        max_tokens: data.max_tokens || 2000,
        temperature: data.temperature || 0.3,
        story_limit: data.story_limit || 1,
        is_active: data.is_active !== false,
        system_prompt: data.system_prompt || DEFAULT_SYSTEM_PROMPT,
        user_prompt: data.user_prompt || '',
        variables: data.variables || [],
        data_source: data.data_source || null,
        wordpress_settings: data.wordpress_settings || initialFormState.wordpress_settings,
        cron_expression: data.cron_expression || '',
        timezone: data.timezone || 'Europe/London',
        max_retries: data.max_retries || 2,
        alert_email: data.alert_email || '',
        run_concurrency: data.run_concurrency || 'sequential',
        output_fields: data.output_fields || initialFormState.output_fields,
      });
      setModalStep(0);
      setShowTemplateModal(true);
    } catch (error) {
      toast.error('Failed to load template');
    }
  };

  const handleSaveTemplate = async () => {
    try {
      let templateId;
      if (editingTemplate) {
        await templatesV2API.update(editingTemplate.id, formData);
        templateId = editingTemplate.id;
        toast.success('Template updated');
      } else {
        const response = await templatesV2API.create(formData);
        templateId = response.data.id;
        toast.success('Template created');
      }
      
      // Handle scheduling separately if cron_expression is set
      if (formData.cron_expression && templateId) {
        try {
          await templatesV2API.schedule(templateId, {
            cron_expression: formData.cron_expression,
            timezone: formData.timezone || 'Europe/London'
          });
          toast.success('Schedule configured');
        } catch (schedErr) {
          console.warn('Schedule setup failed:', schedErr);
          // Don't fail the whole save if scheduling fails
        }
      } else if (!formData.cron_expression && editingTemplate?.cron_expression) {
        // Remove schedule if it was previously set but now cleared
        try {
          await templatesV2API.unschedule(templateId);
        } catch (e) {
          // Ignore unschedule errors
        }
      }
      
      setShowTemplateModal(false);
      loadAllData();
    } catch (error) {
      toast.error('Failed to save template');
    }
  };

  const handleDeleteTemplate = async (id) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    try {
      await templatesV2API.delete(id);
      toast.success('Template deleted');
      loadAllData();
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  const handleRunTemplate = async (template) => {
    setRunLoading(prev => ({ ...prev, [template.id]: true }));
    try {
      const response = await templatesV2API.run(template.id, {});
      if (response.data.is_mock_run) {
        toast.success(`Run completed (demo mode) - ${response.data.stories_generated} stories`);
      } else {
        toast.success(`Run completed - ${response.data.stories_generated} stories`);
      }
      loadAllData();
    } catch (error) {
      toast.error('Failed to run template');
    } finally {
      setRunLoading(prev => ({ ...prev, [template.id]: false }));
    }
  };

  const handleViewRun = async (run) => {
    setSelectedRun(run);
    try {
      const response = await runsV2API.getOne(run.id);
      setRunDetail(response.data);
    } catch (error) {
      toast.error('Failed to load run details');
    }
  };

  const addVariable = () => {
    if (!newVariable.name) return;
    setFormData(prev => ({
      ...prev,
      variables: [...prev.variables, { ...newVariable }]
    }));
    setNewVariable({ name: '', description: '', example: '' });
  };

  const removeVariable = (index) => {
    setFormData(prev => ({
      ...prev,
      variables: prev.variables.filter((_, i) => i !== index)
    }));
  };

  const insertVariable = (varName) => {
    const textarea = document.getElementById('user-prompt-textarea');
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const text = formData.user_prompt;
      const newText = text.substring(0, start) + `{${varName}}` + text.substring(end);
      setFormData(prev => ({ ...prev, user_prompt: newText }));
    }
  };

  const getUsedVariables = () => {
    const matches = formData.user_prompt.match(/\{([^}]+)\}/g) || [];
    return matches.map(m => m.slice(1, -1));
  };

  const modalSteps = [
    { label: 'Configuration', icon: Settings },
    { label: 'Prompt Builder', icon: Code },
    { label: 'Data Source', icon: Database },
    { label: 'WordPress', icon: Globe },
    { label: 'Schedule', icon: Calendar },
    { label: 'Output Schema', icon: FileText },
  ];

  const completedSteps = [
    formData.name && formData.story_type,
    formData.system_prompt && formData.user_prompt,
    true, // Data source is optional
    true, // WordPress settings are optional
    true, // Schedule is optional
    true, // Output schema has defaults
  ];
  if (!user || user.role !== 'admin') return <Navigate to="/" replace />;
  return (
    <div className="space-y-6" data-testid="automated-stories-v2-page">
      {/* Page Header */}
      <div>
        <h1 className="text-3xl font-heading font-bold text-text-primary uppercase tracking-tight">
          Automated Stories
        </h1>
        <p className="text-text-secondary mt-1">Create, schedule, and manage automated content generation templates</p>
      </div>

      {/* Demo Mode Banner */}
      <DemoModeBanner integrations={integrations} onSettingsClick={() => window.location.href = '/settings'} />

      {/* Stats Cards */}
      <div className="grid grid-cols-5 gap-4">
        <Card className="bg-surface-card border-border-default">
          <CardContent className="p-4">
            <p className="text-xs text-text-tertiary uppercase">Templates Active</p>
            <p className="text-2xl font-heading font-bold text-text-primary">{stats.templates_active || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-surface-card border-border-default">
          <CardContent className="p-4">
            <p className="text-xs text-text-tertiary uppercase">Runs Today</p>
            <p className="text-2xl font-heading font-bold text-text-primary">{stats.runs_today || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-surface-card border-border-default">
          <CardContent className="p-4">
            <p className="text-xs text-text-tertiary uppercase">Success Rate</p>
            <p className="text-2xl font-heading font-bold text-green-400">{stats.success_rate || 0}%</p>
          </CardContent>
        </Card>
        <Card className="bg-surface-card border-border-default">
          <CardContent className="p-4">
            <p className="text-xs text-text-tertiary uppercase">Stories Generated</p>
            <p className="text-2xl font-heading font-bold text-accent-primary">{stats.stories_generated || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-surface-card border-border-default">
          <CardContent className="p-4">
            <p className="text-xs text-text-tertiary uppercase">Held for Review</p>
            <p className="text-2xl font-heading font-bold text-amber-400">{stats.held_for_review || 0}</p>
          </CardContent>
        </Card>
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-bg-tertiary">
          <TabsTrigger value="templates">Templates</TabsTrigger>
          <TabsTrigger value="runs">Run History</TabsTrigger>
          {heldQueue.length > 0 && (
            <TabsTrigger value="held" className="relative">
              Held Queue
              <Badge className="ml-2 bg-amber-600 text-white text-xs">{heldQueue.length}</Badge>
            </TabsTrigger>
          )}
        </TabsList>

        {/* Templates Tab */}
        <TabsContent value="templates" className="space-y-6 mt-6">
          <div className="flex justify-end">
            <Button onClick={handleOpenCreateModal} data-testid="create-template-btn">
              <Plus className="w-4 h-4 mr-2" />
              Create Template
            </Button>
          </div>

          <Card className="bg-surface-card border-border-default">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="table-header hover:bg-transparent">
                    <TableHead>Template Name</TableHead>
                    <TableHead>Story Type</TableHead>
                    <TableHead>Publication</TableHead>
                    <TableHead>Schedule</TableHead>
                    <TableHead>Last Run</TableHead>
                    <TableHead>Stories</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-12">
                        <div className="animate-spin w-6 h-6 border-2 border-accent-primary border-t-transparent rounded-full mx-auto" />
                      </TableCell>
                    </TableRow>
                  ) : templates.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-12 text-text-tertiary">
                        <div className="flex flex-col items-center gap-3">
                          <FileText className="w-12 h-12 text-text-tertiary/50" />
                          <p>No templates created yet</p>
                          <Button onClick={handleOpenCreateModal} variant="outline" size="sm">
                            <Plus className="w-4 h-4 mr-2" />
                            Create your first template
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : (
                    templates.map((template) => (
                      <TableRow key={template.id} className="table-row">
                        <TableCell className="table-cell font-medium">{template.name}</TableCell>
                        <TableCell className="table-cell">
                          <Badge variant="outline">
                            {STORY_TYPES.find(t => t.value === template.story_type)?.label || template.story_type}
                          </Badge>
                        </TableCell>
                        <TableCell className="table-cell">{template.publication_name || '-'}</TableCell>
                        <TableCell className="table-cell">
                          {template.cron_expression ? (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger>
                                  <Badge className="bg-green-900/20 text-green-400 border-green-900/50">
                                    <Clock className="w-3 h-3 mr-1" />
                                    Scheduled
                                  </Badge>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p className="font-mono text-xs">{template.cron_expression}</p>
                                  {template.next_run_at && (
                                    <p className="text-xs mt-1">Next: {formatDateTime(template.next_run_at)}</p>
                                  )}
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          ) : (
                            <Badge variant="outline">Manual</Badge>
                          )}
                        </TableCell>
                        <TableCell className="table-cell">
                          {template.last_run ? (
                            <div className="flex items-center gap-2">
                              <span className="text-sm">{formatDate(template.last_run)}</span>
                              <StatusBadge status={template.last_run_status} />
                            </div>
                          ) : '-'}
                        </TableCell>
                        <TableCell className="table-cell">{template.stories_generated || 0}</TableCell>
                        <TableCell className="table-cell">
                          <Badge className={template.is_active ? 'badge-success' : 'badge-neutral'}>
                            {template.is_active ? 'Active' : 'Paused'}
                          </Badge>
                        </TableCell>
                        <TableCell className="table-cell">
                          <div className="flex gap-1">
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    onClick={() => handleRunTemplate(template)}
                                    disabled={runLoading[template.id]}
                                    data-testid={`run-template-${template.id}`}
                                  >
                                    {runLoading[template.id] ? (
                                      <RefreshCw className="w-4 h-4 animate-spin" />
                                    ) : (
                                      <Play className="w-4 h-4" />
                                    )}
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Run Now</TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                            <Button variant="ghost" size="icon" onClick={() => handleOpenEditModal(template)}>
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => handleDeleteTemplate(template.id)}>
                              <Trash2 className="w-4 h-4 text-red-400" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Runs Tab */}
        <TabsContent value="runs" className="space-y-6 mt-6">
          <Card className="bg-surface-card border-border-default">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="table-header hover:bg-transparent">
                    <TableHead>Template</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Trigger</TableHead>
                    <TableHead>Stories</TableHead>
                    <TableHead>Duration</TableHead>
                    <TableHead>Mock</TableHead>
                    <TableHead>Started</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-12">
                        <div className="animate-spin w-6 h-6 border-2 border-accent-primary border-t-transparent rounded-full mx-auto" />
                      </TableCell>
                    </TableRow>
                  ) : runs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-12 text-text-tertiary">
                        No runs yet
                      </TableCell>
                    </TableRow>
                  ) : (
                    runs.map((run) => (
                      <TableRow 
                        key={run.id} 
                        className="table-row cursor-pointer hover:bg-bg-tertiary/50"
                        onClick={() => handleViewRun(run)}
                      >
                        <TableCell className="table-cell font-medium">{run.template_name || '-'}</TableCell>
                        <TableCell className="table-cell">
                          <StatusBadge status={run.status} />
                        </TableCell>
                        <TableCell className="table-cell capitalize">{run.trigger_type}</TableCell>
                        <TableCell className="table-cell">{run.stories_generated || 0}</TableCell>
                        <TableCell className="table-cell">
                          {run.duration_seconds ? `${run.duration_seconds}s` : '-'}
                        </TableCell>
                        <TableCell className="table-cell">
                          {run.is_mock_run ? (
                            <Badge className="bg-amber-900/20 text-amber-400 border-amber-900/50">MOCK</Badge>
                          ) : (
                            <Badge className="bg-green-900/20 text-green-400 border-green-900/50">LIVE</Badge>
                          )}
                        </TableCell>
                        <TableCell className="table-cell">{formatDateTime(run.started_at)}</TableCell>
                        <TableCell className="table-cell">
                          <ChevronRight className="w-4 h-4 text-text-tertiary" />
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Held Queue Tab */}
        {heldQueue.length > 0 && (
          <TabsContent value="held" className="space-y-6 mt-6">
            <Card className="bg-surface-card border-border-default">
              <CardHeader>
                <CardTitle>Stories Held for Review</CardTitle>
                <CardDescription>
                  Stories with confidence score below 0.7 are held for manual review
                </CardDescription>
              </CardHeader>
              <CardContent>
                {heldQueue.map((held) => (
                  <div key={held.id} className="p-4 bg-bg-tertiary rounded-lg mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-text-primary">{held.template_name}</span>
                      <Badge className="bg-amber-900/20 text-amber-400 border-amber-900/50">
                        Confidence: {(held.confidence_score * 100).toFixed(0)}%
                      </Badge>
                    </div>
                    <p className="text-sm text-text-secondary mb-3">{held.hold_reason}</p>
                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        onClick={() => heldQueueAPI.review(held.id, { action: 'approved' }).then(loadAllData)}
                      >
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Approve
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => heldQueueAPI.review(held.id, { action: 'rejected' }).then(loadAllData)}
                      >
                        <XCircle className="w-4 h-4 mr-1" />
                        Reject
                      </Button>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* Template Builder Modal */}
      <Dialog open={showTemplateModal} onOpenChange={setShowTemplateModal}>
        <DialogContent className="sm:max-w-4xl bg-surface-card border-border-default max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-text-primary text-xl">
              {editingTemplate ? 'Edit Template' : 'Create Template'}
            </DialogTitle>
            <DialogDescription>
              Configure your story automation template step by step
            </DialogDescription>
          </DialogHeader>
          
          {/* Step Progress */}
          <div className="flex items-center gap-2 py-4 border-b border-border-default">
            {modalSteps.map((step, index) => {
              const Icon = step.icon;
              const isActive = index === modalStep;
              const isComplete = completedSteps[index];
              
              return (
                <React.Fragment key={step.label}>
                  <button
                    onClick={() => setModalStep(index)}
                    className={cn(
                      "flex items-center gap-2 px-3 py-2 rounded-lg transition-colors",
                      isActive ? "bg-accent-primary text-white" : "text-text-secondary hover:bg-bg-tertiary",
                      isComplete && !isActive && "text-green-400"
                    )}
                  >
                    <Icon className="w-4 h-4" />
                    <span className="text-sm font-medium hidden lg:inline">{step.label}</span>
                    {isComplete && !isActive && <CheckCircle className="w-3 h-3" />}
                  </button>
                  {index < modalSteps.length - 1 && (
                    <div className={cn(
                      "w-4 h-0.5",
                      isComplete ? "bg-green-400" : "bg-border-default"
                    )} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
          
          {/* Step Content */}
          <div className="flex-1 overflow-y-auto py-4">
            {/* Tab 1: Configuration */}
            {modalStep === 0 && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Template Name <span className="text-red-400">*</span></Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="e.g., Weekly Market Report"
                      className="bg-bg-tertiary border-border-default"
                    />
                  </div>
                  <div>
                    <Label>Story Type <span className="text-red-400">*</span></Label>
                    <DynamicSelect
                      fieldType="story_type"
                      value={formData.story_type}
                      onValueChange={(v) => setFormData(prev => ({ ...prev, story_type: v }))}
                      placeholder="Select type"
                      triggerClassName="bg-bg-tertiary border-border-default"
                    />
                  </div>
                </div>

                <div>
                  <Label>Description</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Brief description of what this template generates"
                    className="bg-bg-tertiary border-border-default"
                  />
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Publication</Label>
                    <Select value={formData.publication_id || undefined} onValueChange={(v) => setFormData(prev => ({ ...prev, publication_id: v }))}>
                      <SelectTrigger className="bg-bg-tertiary border-border-default">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        {publications.map((pub) => (
                          <SelectItem key={pub.id} value={pub.id}>{pub.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Client (Optional)</Label>
                    <Select value={formData.client_id || undefined} onValueChange={(v) => setFormData(prev => ({ ...prev, client_id: v }))}>
                      <SelectTrigger className="bg-bg-tertiary border-border-default">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        {clients.map((client) => (
                          <SelectItem key={client.id} value={client.id}>{client.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Default Byline</Label>
                    <Input
                      value={formData.byline}
                      onChange={(e) => setFormData(prev => ({ ...prev, byline: e.target.value }))}
                      placeholder="Author name"
                      className="bg-bg-tertiary border-border-default"
                    />
                  </div>
                </div>

                <Separator />

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>
                      Grok Max Tokens
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger>
                            <AlertCircle className="w-3 h-3 ml-1 inline text-text-tertiary" />
                          </TooltipTrigger>
                          <TooltipContent>Maximum tokens for Grok response</TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </Label>
                    <Input
                      type="number"
                      value={formData.max_tokens}
                      onChange={(e) => setFormData(prev => ({ ...prev, max_tokens: parseInt(e.target.value) || 2000 }))}
                      className="bg-bg-tertiary border-border-default"
                    />
                  </div>
                  <div>
                    <Label>
                      Temperature
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger>
                            <AlertCircle className="w-3 h-3 ml-1 inline text-text-tertiary" />
                          </TooltipTrigger>
                          <TooltipContent>Lower = factual, Higher = creative (0-1)</TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </Label>
                    <Input
                      type="number"
                      step="0.05"
                      min="0"
                      max="1"
                      value={formData.temperature}
                      onChange={(e) => setFormData(prev => ({ ...prev, temperature: parseFloat(e.target.value) || 0.3 }))}
                      className="bg-bg-tertiary border-border-default"
                    />
                  </div>
                  <div>
                    <Label>Stories Per Run</Label>
                    <Input
                      type="number"
                      min="1"
                      max="10"
                      value={formData.story_limit}
                      onChange={(e) => setFormData(prev => ({ ...prev, story_limit: parseInt(e.target.value) || 1 }))}
                      className="bg-bg-tertiary border-border-default"
                    />
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Switch
                    checked={formData.is_active}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
                  />
                  <Label>Template Active</Label>
                </div>
              </div>
            )}

            {/* Tab 2: Prompt Builder */}
            {modalStep === 1 && (
              <div className="space-y-4">
                {/* Variable Manager */}
                <div className="bg-bg-tertiary p-4 rounded-lg">
                  <Label className="text-sm font-medium mb-2 block">Variable Manager</Label>
                  <p className="text-xs text-text-tertiary mb-3">
                    Define variables to use in your prompts. Click a chip to insert it into the user prompt.
                  </p>
                  
                  <div className="flex flex-wrap gap-2 mb-3">
                    <Badge 
                      variant="outline" 
                      className="cursor-pointer hover:bg-accent-primary/20"
                      onClick={() => insertVariable('data_payload')}
                    >
                      <Variable className="w-3 h-3 mr-1" />
                      {'{data_payload}'} (reserved)
                    </Badge>
                    {formData.variables.map((v, i) => (
                      <Badge 
                        key={i}
                        variant="outline" 
                        className="cursor-pointer hover:bg-accent-primary/20 group"
                        onClick={() => insertVariable(v.name)}
                      >
                        <Variable className="w-3 h-3 mr-1" />
                        {`{${v.name}}`}
                        <button 
                          onClick={(e) => { e.stopPropagation(); removeVariable(i); }}
                          className="ml-1 opacity-0 group-hover:opacity-100"
                        >
                          <XCircle className="w-3 h-3 text-red-400" />
                        </button>
                      </Badge>
                    ))}
                  </div>

                  <div className="grid grid-cols-4 gap-2">
                    <Input
                      placeholder="Variable name"
                      value={newVariable.name}
                      onChange={(e) => setNewVariable(prev => ({ ...prev, name: e.target.value.replace(/\s/g, '_') }))}
                      className="bg-bg-secondary border-border-default"
                    />
                    <Input
                      placeholder="Description"
                      value={newVariable.description}
                      onChange={(e) => setNewVariable(prev => ({ ...prev, description: e.target.value }))}
                      className="bg-bg-secondary border-border-default"
                    />
                    <Input
                      placeholder="Example value"
                      value={newVariable.example}
                      onChange={(e) => setNewVariable(prev => ({ ...prev, example: e.target.value }))}
                      className="bg-bg-secondary border-border-default"
                    />
                    <Button onClick={addVariable} variant="outline" size="sm">
                      <Plus className="w-4 h-4 mr-1" />
                      Add
                    </Button>
                  </div>
                </div>

                {/* System Prompt */}
                <div>
                  <Label>Grok System Prompt <span className="text-red-400">*</span></Label>
                  <Textarea
                    value={formData.system_prompt}
                    onChange={(e) => setFormData(prev => ({ ...prev, system_prompt: e.target.value }))}
                    className="bg-bg-tertiary border-border-default min-h-[120px] font-mono text-sm"
                    placeholder="System instructions for Grok..."
                  />
                </div>

                {/* User Prompt */}
                <div>
                  <Label>Grok User Prompt Template <span className="text-red-400">*</span></Label>
                  <Textarea
                    id="user-prompt-textarea"
                    value={formData.user_prompt}
                    onChange={(e) => setFormData(prev => ({ ...prev, user_prompt: e.target.value }))}
                    className="bg-bg-tertiary border-border-default min-h-[200px] font-mono text-sm"
                    placeholder="Use {variable_name} for dynamic values..."
                  />
                  
                  {/* Used Variables Display */}
                  <div className="mt-2 flex items-center gap-2 text-xs text-text-tertiary">
                    <span>Variables used:</span>
                    {getUsedVariables().length > 0 ? (
                      getUsedVariables().map((v, i) => (
                        <Badge key={i} variant="outline" className="text-xs">
                          {v}
                        </Badge>
                      ))
                    ) : (
                      <span className="text-text-tertiary/50">None</span>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Tab 3: Data Source */}
            {modalStep === 2 && (
              <div className="space-y-4">
                <div>
                  <Label>Data Source Type</Label>
                  <Select 
                    value={formData.data_source?.source_type || 'none'} 
                    onValueChange={(v) => setFormData(prev => ({
                      ...prev,
                      data_source: v && v !== 'none' ? { ...prev.data_source, source_type: v } : null
                    }))}
                  >
                    <SelectTrigger className="bg-bg-tertiary border-border-default">
                      <SelectValue placeholder="Select data source (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {DATA_SOURCE_TYPES.map((type) => (
                        <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {formData.data_source?.source_type === 'data_intelligence' && (
                  <div className="space-y-4 p-4 bg-bg-tertiary rounded-lg">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Record Type Filter</Label>
                        <Select 
                          value={formData.data_source?.di_record_type || 'all'} 
                          onValueChange={(v) => setFormData(prev => ({
                            ...prev,
                            data_source: { ...prev.data_source, di_record_type: v }
                          }))}
                        >
                          <SelectTrigger className="bg-bg-secondary border-border-default">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All Types</SelectItem>
                            <SelectItem value="organisation">Organisations</SelectItem>
                            <SelectItem value="person">People</SelectItem>
                            <SelectItem value="event">Events</SelectItem>
                            <SelectItem value="location">Locations</SelectItem>
                            <SelectItem value="property">Properties</SelectItem>
                            <SelectItem value="document">Documents</SelectItem>
                            <SelectItem value="relationship">Relationships</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Max Records</Label>
                        <Input
                          type="number"
                          value={formData.data_source?.di_max_records || 10}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            data_source: { ...prev.data_source, di_max_records: parseInt(e.target.value) || 10 }
                          }))}
                          min={1}
                          max={100}
                          className="bg-bg-secondary border-border-default"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <Label>Tag Filter (comma-separated)</Label>
                      <Input
                        value={formData.data_source?.di_tags || ''}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          data_source: { ...prev.data_source, di_tags: e.target.value }
                        }))}
                        placeholder="e.g., insolvency, procurement"
                        className="bg-bg-secondary border-border-default"
                      />
                    </div>
                    
                    <div>
                      <Label>Sort By</Label>
                      <Select 
                        value={formData.data_source?.di_sort || 'created_at_desc'} 
                        onValueChange={(v) => setFormData(prev => ({
                          ...prev,
                          data_source: { ...prev.data_source, di_sort: v }
                        }))}
                      >
                        <SelectTrigger className="bg-bg-secondary border-border-default">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="created_at_desc">Newest First</SelectItem>
                          <SelectItem value="created_at_asc">Oldest First</SelectItem>
                          <SelectItem value="updated_at_desc">Recently Updated</SelectItem>
                          <SelectItem value="title_asc">Title (A-Z)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="flex items-center justify-between p-3 bg-bg-secondary rounded-lg">
                      <div>
                        <Label className="text-sm">Include Related Records</Label>
                        <p className="text-xs text-text-tertiary">Fetch related entities linked to primary records</p>
                      </div>
                      <Switch
                        checked={formData.data_source?.di_include_related || false}
                        onCheckedChange={(v) => setFormData(prev => ({
                          ...prev,
                          data_source: { ...prev.data_source, di_include_related: v }
                        }))}
                      />
                    </div>
                    
                    <div className="bg-blue-900/20 border border-blue-800 p-4 rounded-lg">
                      <p className="text-sm text-blue-400">
                        <strong>Data Intelligence records</strong> will be serialized as JSON and available in your prompt via the <code className="bg-bg-tertiary px-1 rounded">{'{di_records}'}</code> variable.
                      </p>
                    </div>
                  </div>
                )}

                {formData.data_source?.source_type === 'api' && (
                  <div className="space-y-4 p-4 bg-bg-tertiary rounded-lg">
                    <div>
                      <Label>API Endpoint URL</Label>
                      <Input
                        value={formData.data_source?.endpoint_url || ''}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          data_source: { ...prev.data_source, endpoint_url: e.target.value }
                        }))}
                        placeholder="https://api.example.com/data"
                        className="bg-bg-secondary border-border-default font-mono"
                      />
                    </div>
                    <div>
                      <Label>Authorization Header (Optional)</Label>
                      <Input
                        value={formData.data_source?.auth_header || ''}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          data_source: { ...prev.data_source, auth_header: e.target.value }
                        }))}
                        placeholder="Bearer your-token"
                        className="bg-bg-secondary border-border-default font-mono"
                      />
                    </div>
                  </div>
                )}

                {formData.data_source?.source_type === 'database' && (
                  <div className="space-y-4 p-4 bg-bg-tertiary rounded-lg">
                    <div>
                      <Label>SQL Query</Label>
                      <Textarea
                        value={formData.data_source?.sql_query || ''}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          data_source: { ...prev.data_source, sql_query: e.target.value }
                        }))}
                        placeholder="SELECT * FROM records WHERE..."
                        className="bg-bg-secondary border-border-default font-mono min-h-[100px]"
                      />
                    </div>
                  </div>
                )}

                {formData.data_source?.source_type === 'google_sheets' && (
                  <div className="space-y-4 p-4 bg-bg-tertiary rounded-lg">
                    <div>
                      <Label>Google Sheet ID</Label>
                      <Input
                        value={formData.data_source?.google_sheet_id || ''}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          data_source: { ...prev.data_source, google_sheet_id: e.target.value }
                        }))}
                        placeholder="1BxiMVs0XRA5nFMdKvBdBZjgmUUqptlbs74OgvE2upms"
                        className="bg-bg-secondary border-border-default font-mono"
                      />
                    </div>
                  </div>
                )}

                {formData.data_source?.source_type && (
                  <div className="bg-accent-primary/10 p-4 rounded-lg">
                    <p className="text-sm text-accent-primary">
                      When a data source is connected, the <code className="bg-bg-tertiary px-1 rounded">{'{data_payload}'}</code> variable becomes available in your prompt template.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Tab 4: WordPress Settings */}
            {modalStep === 3 && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Post Status on Publish</Label>
                    <Select 
                      value={formData.wordpress_settings.post_status} 
                      onValueChange={(v) => setFormData(prev => ({
                        ...prev,
                        wordpress_settings: { ...prev.wordpress_settings, post_status: v }
                      }))}
                    >
                      <SelectTrigger className="bg-bg-tertiary border-border-default">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="draft">Draft</SelectItem>
                        <SelectItem value="publish">Publish</SelectItem>
                        <SelectItem value="pending">Pending Review</SelectItem>
                        <SelectItem value="private">Private</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Default Author</Label>
                    <Input
                      value={formData.wordpress_settings.default_author}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        wordpress_settings: { ...prev.wordpress_settings, default_author: e.target.value }
                      }))}
                      placeholder="Author username"
                      className="bg-bg-tertiary border-border-default"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>
                      Categories
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger>
                            <AlertCircle className="w-3 h-3 ml-1 inline text-text-tertiary" />
                          </TooltipTrigger>
                          <TooltipContent>Must match existing WordPress categories</TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </Label>
                    <Input
                      value={formData.wordpress_settings.categories}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        wordpress_settings: { ...prev.wordpress_settings, categories: e.target.value }
                      }))}
                      placeholder="Business, Technology"
                      className="bg-bg-tertiary border-border-default"
                    />
                  </div>
                  <div>
                    <Label>Tags Template</Label>
                    <Input
                      value={formData.wordpress_settings.tags_template}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        wordpress_settings: { ...prev.wordpress_settings, tags_template: e.target.value }
                      }))}
                      placeholder="{company_name}, earnings, {quarter}"
                      className="bg-bg-tertiary border-border-default font-mono text-sm"
                    />
                  </div>
                </div>

                <div className="flex items-center gap-6">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={formData.wordpress_settings.duplicate_prevention}
                      onCheckedChange={(checked) => setFormData(prev => ({
                        ...prev,
                        wordpress_settings: { ...prev.wordpress_settings, duplicate_prevention: checked }
                      }))}
                    />
                    <Label>Duplicate Prevention</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={formData.wordpress_settings.force_republish}
                      onCheckedChange={(checked) => setFormData(prev => ({
                        ...prev,
                        wordpress_settings: { ...prev.wordpress_settings, force_republish: checked }
                      }))}
                    />
                    <Label>Force Republish</Label>
                  </div>
                </div>

                <Separator />

                {/* Image Generation Section */}
                <div className="bg-bg-tertiary p-4 rounded-lg">
                  <h4 className="font-medium text-text-primary mb-3 flex items-center gap-2">
                    <Image className="w-4 h-4" />
                    Image Generation
                  </h4>

                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <Label>Image Provider</Label>
                      <Select 
                        value={formData.wordpress_settings.image_provider} 
                        onValueChange={(v) => setFormData(prev => ({
                          ...prev,
                          wordpress_settings: { ...prev.wordpress_settings, image_provider: v }
                        }))}
                      >
                        <SelectTrigger className="bg-bg-secondary border-border-default">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {IMAGE_PROVIDERS.map((p) => (
                            <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Image Style Preset</Label>
                      <Select 
                        value={formData.wordpress_settings.image_style} 
                        onValueChange={(v) => setFormData(prev => ({
                          ...prev,
                          wordpress_settings: { ...prev.wordpress_settings, image_style: v }
                        }))}
                        disabled={formData.wordpress_settings.image_provider === 'none'}
                      >
                        <SelectTrigger className="bg-bg-secondary border-border-default">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {IMAGE_STYLES.map((s) => (
                            <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {formData.wordpress_settings.image_provider !== 'none' && (
                    <>
                      <div className="mb-4">
                        <Label>Base Image Prompt Template</Label>
                        <Textarea
                          value={formData.wordpress_settings.image_prompt_template}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            wordpress_settings: { ...prev.wordpress_settings, image_prompt_template: e.target.value }
                          }))}
                          placeholder="Professional editorial image representing {company_name} in the {sector} industry, dark blue corporate aesthetic..."
                          className="bg-bg-secondary border-border-default font-mono text-sm"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Image Size</Label>
                          <Select 
                            value={formData.wordpress_settings.image_size} 
                            onValueChange={(v) => setFormData(prev => ({
                              ...prev,
                              wordpress_settings: { ...prev.wordpress_settings, image_size: v }
                            }))}
                          >
                            <SelectTrigger className="bg-bg-secondary border-border-default">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="1792x1024">1792x1024 (Landscape)</SelectItem>
                              <SelectItem value="1024x1024">1024x1024 (Square)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Alt Text Template</Label>
                          <Input
                            value={formData.wordpress_settings.alt_text_template}
                            onChange={(e) => setFormData(prev => ({
                              ...prev,
                              wordpress_settings: { ...prev.wordpress_settings, alt_text_template: e.target.value }
                            }))}
                            placeholder="{company_name} - {headline}"
                            className="bg-bg-secondary border-border-default font-mono text-sm"
                          />
                        </div>
                      </div>

                      <div className="flex items-center gap-2 mt-4">
                        <Switch
                          checked={formData.wordpress_settings.upload_to_wp}
                          onCheckedChange={(checked) => setFormData(prev => ({
                            ...prev,
                            wordpress_settings: { ...prev.wordpress_settings, upload_to_wp: checked }
                          }))}
                        />
                        <Label>Upload to WordPress Media Library</Label>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Tab 5: Schedule */}
            {modalStep === 4 && (
              <div className="space-y-4">
                <div className="flex flex-wrap gap-2 mb-4">
                  {CRON_PRESETS.map((preset) => (
                    <Button
                      key={preset.value}
                      variant={formData.cron_expression === preset.value ? "default" : "outline"}
                      size="sm"
                      onClick={() => setFormData(prev => ({ ...prev, cron_expression: preset.value }))}
                    >
                      {preset.label}
                    </Button>
                  ))}
                  <Button
                    variant={!CRON_PRESETS.find(p => p.value === formData.cron_expression) && formData.cron_expression ? "default" : "outline"}
                    size="sm"
                    onClick={() => setFormData(prev => ({ ...prev, cron_expression: '' }))}
                  >
                    Custom
                  </Button>
                </div>

                <div>
                  <Label>Cron Expression</Label>
                  <Input
                    value={formData.cron_expression}
                    onChange={(e) => setFormData(prev => ({ ...prev, cron_expression: e.target.value }))}
                    placeholder="0 9 * * 1-5 (Weekdays at 9am)"
                    className="bg-bg-tertiary border-border-default font-mono"
                  />
                  {formData.cron_expression && (
                    <div className="mt-2 p-3 bg-bg-tertiary rounded text-sm font-mono">
                      <div className="grid grid-cols-5 gap-2 text-center text-xs text-text-tertiary mb-1">
                        <span>Minute</span>
                        <span>Hour</span>
                        <span>Day</span>
                        <span>Month</span>
                        <span>Weekday</span>
                      </div>
                      <div className="grid grid-cols-5 gap-2 text-center">
                        {formData.cron_expression.split(' ').slice(0, 5).map((part, i) => (
                          <span key={i} className="bg-accent-primary/20 text-accent-primary px-2 py-1 rounded">{part}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Timezone</Label>
                    <Select 
                      value={formData.timezone} 
                      onValueChange={(v) => setFormData(prev => ({ ...prev, timezone: v }))}
                    >
                      <SelectTrigger className="bg-bg-tertiary border-border-default">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Europe/London">Europe/London (GMT/BST)</SelectItem>
                        <SelectItem value="America/New_York">America/New_York (EST/EDT)</SelectItem>
                        <SelectItem value="UTC">UTC</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Max Retries on Failure</Label>
                    <Input
                      type="number"
                      min="0"
                      max="5"
                      value={formData.max_retries}
                      onChange={(e) => setFormData(prev => ({ ...prev, max_retries: parseInt(e.target.value) || 2 }))}
                      className="bg-bg-tertiary border-border-default"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Alert Email on Failure</Label>
                    <Input
                      type="email"
                      value={formData.alert_email}
                      onChange={(e) => setFormData(prev => ({ ...prev, alert_email: e.target.value }))}
                      placeholder="alerts@company.com"
                      className="bg-bg-tertiary border-border-default"
                    />
                  </div>
                  <div>
                    <Label>Run Concurrency</Label>
                    <Select 
                      value={formData.run_concurrency} 
                      onValueChange={(v) => setFormData(prev => ({ ...prev, run_concurrency: v }))}
                    >
                      <SelectTrigger className="bg-bg-tertiary border-border-default">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="sequential">Sequential</SelectItem>
                        <SelectItem value="parallel_3">Parallel (up to 3)</SelectItem>
                        <SelectItem value="parallel_10">Parallel (up to 10)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            )}

            {/* Tab 6: Output Schema */}
            {modalStep === 5 && (
              <div className="space-y-4">
                <div className="bg-bg-tertiary p-4 rounded-lg">
                  <p className="text-sm text-text-secondary mb-4">
                    Select which fields Grok should return. Required fields are always enabled.
                  </p>
                  
                  <div className="grid grid-cols-2 gap-3">
                    {OUTPUT_FIELDS.map((field) => (
                      <div 
                        key={field.key}
                        className={cn(
                          "flex items-center justify-between p-3 rounded border",
                          field.required ? "bg-bg-secondary border-border-default opacity-75" : "bg-bg-secondary border-border-default"
                        )}
                      >
                        <div>
                          <span className="text-text-primary">{field.label}</span>
                          {field.required && (
                            <Badge className="ml-2 text-xs" variant="outline">Required</Badge>
                          )}
                        </div>
                        <Switch
                          checked={field.required || formData.output_fields[field.key]}
                          disabled={field.required}
                          onCheckedChange={(checked) => setFormData(prev => ({
                            ...prev,
                            output_fields: { ...prev.output_fields, [field.key]: checked }
                          }))}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* JSON Preview */}
                <div>
                  <Label>Output Schema Preview</Label>
                  <pre className="bg-bg-tertiary p-4 rounded-lg text-sm font-mono text-text-secondary overflow-auto max-h-[200px]">
{JSON.stringify(
  Object.fromEntries(
    OUTPUT_FIELDS
      .filter(f => f.required || formData.output_fields[f.key])
      .map(f => [f.key, f.key.includes('array') ? ['...'] : f.required ? '(required)' : '(optional)'])
  ),
  null,
  2
)}
                  </pre>
                </div>

                {/* Confidence Routing Info */}
                <div className="bg-amber-900/20 border border-amber-700/50 p-4 rounded-lg">
                  <h4 className="font-medium text-amber-300 mb-2">Confidence Routing</h4>
                  <div className="text-sm text-amber-400/80 space-y-1">
                    <p>• <strong>0.9 - 1.0:</strong> Auto-publish at configured post status</p>
                    <p>• <strong>0.7 - 0.89:</strong> Force to draft, mark for review</p>
                    <p>• <strong>Below 0.7:</strong> Held in queue, alert email sent</p>
                  </div>
                </div>

                {formData.output_fields.seo_title || formData.output_fields.seo_description ? (
                  <div className="bg-accent-primary/10 p-4 rounded-lg text-sm">
                    <p className="text-accent-primary">
                      <strong>SEO Note:</strong> seo_title and seo_description are automatically written to Yoast SEO and RankMath meta fields via the WordPress REST API when enabled.
                    </p>
                  </div>
                ) : null}
              </div>
            )}
          </div>

          {/* Footer */}
          <DialogFooter className="border-t border-border-default pt-4">
            <div className="flex items-center justify-between w-full">
              <div>
                {modalStep > 0 && (
                  <Button variant="outline" onClick={() => setModalStep(prev => prev - 1)}>
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    Back
                  </Button>
                )}
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setShowTemplateModal(false)}>Cancel</Button>
                {modalStep < 5 ? (
                  <Button onClick={() => setModalStep(prev => prev + 1)}>
                    Continue
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                ) : (
                  <Button 
                    onClick={handleSaveTemplate}
                    className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                    disabled={!formData.name || !formData.story_type || !formData.system_prompt || !formData.user_prompt}
                    data-testid="save-template-btn"
                  >
                    {editingTemplate ? 'Update Template' : 'Create Template'}
                  </Button>
                )}
              </div>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Run Detail Sheet */}
      <Sheet open={!!selectedRun} onOpenChange={(open) => !open && setSelectedRun(null)}>
        <SheetContent className="w-full sm:max-w-2xl bg-surface-card border-border-default overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="text-text-primary flex items-center gap-2">
              Run Details
              {runDetail?.is_mock_run && (
                <Badge className="bg-amber-900/20 text-amber-400 border-amber-900/50">MOCK RUN</Badge>
              )}
            </SheetTitle>
          </SheetHeader>

          {runDetail ? (
            <div className="mt-6 space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-bg-tertiary p-3 rounded">
                  <p className="text-xs text-text-tertiary">Status</p>
                  <StatusBadge status={runDetail.status} />
                </div>
                <div className="bg-bg-tertiary p-3 rounded">
                  <p className="text-xs text-text-tertiary">Duration</p>
                  <p className="text-text-primary">{runDetail.duration_seconds}s</p>
                </div>
                <div className="bg-bg-tertiary p-3 rounded">
                  <p className="text-xs text-text-tertiary">Stories Generated</p>
                  <p className="text-text-primary text-xl font-bold">{runDetail.stories_generated}</p>
                </div>
                <div className="bg-bg-tertiary p-3 rounded">
                  <p className="text-xs text-text-tertiary">Trigger</p>
                  <p className="text-text-primary capitalize">{runDetail.trigger_type}</p>
                </div>
              </div>

              {runDetail.error_log && (
                <div className="bg-red-900/20 border border-red-700/50 p-4 rounded-lg">
                  <p className="text-red-400 text-sm font-mono whitespace-pre-wrap">{runDetail.error_log}</p>
                </div>
              )}

              {/* Stories */}
              {runDetail.stories?.length > 0 && (
                <div>
                  <h4 className="font-medium text-text-primary mb-3">Generated Stories</h4>
                  <div className="space-y-3">
                    {runDetail.stories.map((story, i) => (
                      <div key={story.id} className="bg-bg-tertiary p-4 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs text-text-tertiary">Story #{i + 1}</span>
                          <div className="flex gap-2">
                            {story.is_mock_grok && (
                              <Badge variant="outline" className="text-xs">MOCK GROK</Badge>
                            )}
                            {story.is_mock_wp && (
                              <Badge variant="outline" className="text-xs">MOCK WP</Badge>
                            )}
                            <Badge className={
                              story.publish_status === 'auto_published' ? 'bg-green-900/20 text-green-400' :
                              story.publish_status === 'drafted' ? 'bg-amber-900/20 text-amber-400' :
                              story.publish_status === 'held' ? 'bg-red-900/20 text-red-400' :
                              'bg-slate-800 text-slate-400'
                            }>
                              {story.publish_status}
                            </Badge>
                          </div>
                        </div>

                        {story.parsed_json?.headline && (
                          <p className="font-medium text-text-primary mb-2">{story.parsed_json.headline}</p>
                        )}

                        <div className="grid grid-cols-3 gap-2 text-xs">
                          <div>
                            <span className="text-text-tertiary">Confidence:</span>
                            <span className="ml-1 text-text-secondary">{(story.confidence_score * 100).toFixed(0)}%</span>
                          </div>
                          <div>
                            <span className="text-text-tertiary">Words:</span>
                            <span className="ml-1 text-text-secondary">{story.word_count || '-'}</span>
                          </div>
                          {story.wp_post_url && (
                            <div>
                              <a href={story.wp_post_url} target="_blank" rel="noopener noreferrer" className="text-accent-primary hover:underline">
                                View Post →
                              </a>
                            </div>
                          )}
                        </div>

                        {story.image_url && (
                          <div className="mt-3">
                            <img 
                              src={story.image_url} 
                              alt="Generated" 
                              className="w-full h-32 object-cover rounded"
                            />
                          </div>
                        )}

                        {story.error_message && (
                          <p className="mt-2 text-xs text-red-400">{story.error_message}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin w-6 h-6 border-2 border-accent-primary border-t-transparent rounded-full" />
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

export default AutomatedStoriesV2;
